import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Router, RouterModule, UrlSegment } from '@angular/router';
import { ChangableComponent } from '@app/models/changable.component';
import { RedPointDirective } from '@core/components/red-points/red-point/red-point.directive';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { IconComponent } from '@shared/icon/icon.component';
import { CLibraryFilterData, ERedPointsEvents } from '@store/models';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { ContentTypeEnum, EContentPanelRoute, IContentFilterBlock, Router as BaseRouter } from 'lingo2-models';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-categories-item',
  templateUrl: './categories-item.component.html',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule, RedPointDirective, IconComponent],
  styleUrls: ['./categories-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesItemComponent extends ChangableComponent implements OnInit {
  @Input() public set panel(value: IContentFilterBlock) {
    this._panel = value;
  }
  public get panel() {
    return this._panel;
  }
  @Input() link: UrlSegment;
  @Input() displayMode: 'default' | 'simple' = 'default';
  @Output() makeFavorite: EventEmitter<IContentFilterBlock> = new EventEmitter<IContentFilterBlock>();
  @Output() checkActiveTab = new EventEmitter<string>();

  private _panel: IContentFilterBlock;
  private _libraryFilterData: CLibraryFilterData;
  private libRouter = new LibraryRouter();

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private store: Store,
    private router: Router,
    protected readonly cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  ngOnInit() {
    this.store
      .select(getLibraryRouteData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (libraryFilterData) => {
          this._libraryFilterData = libraryFilterData;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public getIconName() {
    switch (this._panel.type) {
      case 'contentType':
        return `content-type-${ContentTypeEnum[+this._panel?.item]}`;
      case 'userServiceCategory':
        return `user-service-category-${this._panel?.item['code']}`;
      default:
        return null;
    }
  }

  public isActive(): boolean {
    if (this.isProfilePage()) {
      return decodeURIComponent(this._libraryFilterData?.url)?.split('/')[3] === this._panel?.code;
    } else if (this._libraryFilterData?.baseRoute === EContentPanelRoute.lessons) {
      const code = this._libraryFilterData?.contentTypeCodes.find(
        (_tc) => _tc === BaseRouter.getNameByContentType(this._panel?.id),
      );
      this.checkActiveTab.emit(code);
      return !!code;
    } else if (this._libraryFilterData?.baseRoute === EContentPanelRoute.classes) {
      return !!this._libraryFilterData?.contentTypeCodes.find((_tc) => _tc === this._panel?.code);
    } else if (this.isCustomPage()) {
      return String(this._libraryFilterData?.baseRoute) === this._panel?.code;
    }
    return decodeURIComponent(this._libraryFilterData?.url)?.split('/').pop() === this._panel?.code;
  }

  public isCustomPage(): boolean {
    const segmentBaseRoute = this._libraryFilterData?.baseRoute?.split('/')[0];
    return ['my-classes', 'my-library', 'favorites', 'my-feed'].includes(segmentBaseRoute);
  }

  public isProfilePage(): boolean {
    const segmentBaseRoute = decodeURIComponent(this._libraryFilterData?.url)?.split('/')[1];
    return ['u'].includes(segmentBaseRoute);
  }

  public get isPageWithResetFilter(): boolean {
    const segmentBaseRoute = decodeURIComponent(this._libraryFilterData?.url)?.split('/')[1];
    return ['classes', 'lessons'].includes(segmentBaseRoute);
  }

  public onMakeFavorite(event: Event, panel: IContentFilterBlock) {
    event.stopPropagation();
    this.makeFavorite.emit(panel);
  }

  public onClick(e: Event) {
    if (this.displayMode !== 'simple' || !this.isActive() || !this.isPageWithResetFilter) {
      return;
    }
    e.preventDefault();
    e.cancelBubble = true;
    this.libRouter.setBaseRoute(this._libraryFilterData?.baseRoute);
    this.libRouter.setContentMode(this._libraryFilterData?.contentMode);
    this.libRouter.setSubjects(this._libraryFilterData?.subjectCodes);
    this.libRouter.removeContentTypeByName(BaseRouter.getNameByContentType(this._panel?.id));
    this.router.navigate([this.libRouter.getLink().path]).then();
  }

  public redPointEvent(): ERedPointsEvents {
    switch (this.panel.code) {
      case String(EContentPanelRoute['my-classes/events']):
        return ERedPointsEvents.update_schedule;
      case String(EContentPanelRoute['my-classes/classrooms']):
        return ERedPointsEvents.classroom_new_invitation;
      case String(EContentPanelRoute['my-library/lessons']):
        return ERedPointsEvents.my_library_lessons;
      case String(EContentPanelRoute['my-library/services']):
        return ERedPointsEvents.my_library_services;
      case String(EContentPanelRoute['my-library/collections']):
        return ERedPointsEvents.my_library_collections;
      default:
        return null;
    }
  }
}
